import { useState } from "react";
import { toast } from "sonner";
import { Copy, Check } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const inputCls =
  "w-full bg-obsidian border border-white/10 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-volt/60 transition-colors duration-300";

export default function ProfileTab({ user }) {
  const { setUser } = useAuth();
  const [copied, setCopied] = useState(false);
  const [profile, setProfile] = useState({ name: user.name, phone: user.phone || "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [busyProfile, setBusyProfile] = useState(false);
  const [busyPw, setBusyPw] = useState(false);

  const copyId = async () => {
    try {
      await navigator.clipboard.writeText(user.zid);
      setCopied(true);
      toast.success("Zork ID copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed");
    }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setBusyProfile(true);
    try {
      const res = await api.patch("/auth/profile", profile);
      setUser(res.data);
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyProfile(false);
    }
  };

  const changePw = async (e) => {
    e.preventDefault();
    setBusyPw(true);
    try {
      await api.post("/auth/change-password", pw);
      toast.success("Password changed");
      setPw({ current_password: "", new_password: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyPw(false);
    }
  };

  return (
    <div data-testid="profile-tab" className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <section className="border border-white/5 bg-panel/40 p-8">
        <h3 className="font-display font-semibold text-xl tracking-tight mb-8">Identity</h3>
        <div className="flex items-center justify-between border border-volt/30 bg-volt/5 px-5 py-4 mb-8">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">Your Zork ID</p>
            <p data-testid="profile-zid" className="font-mono text-2xl text-volt tracking-[0.15em] mt-1">{user.zid}</p>
          </div>
          <button data-testid="copy-zid-btn" onClick={copyId} className="text-volt hover:text-white transition-colors duration-300" aria-label="Copy Zork ID">
            {copied ? <Check size={20} /> : <Copy size={20} />}
          </button>
        </div>
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">Registered email</p>
        <p data-testid="profile-email" className="text-sm text-slate-300 mb-8">{user.email}</p>
        <form onSubmit={saveProfile} data-testid="profile-form" className="space-y-4">
          <input data-testid="profile-name-input" required minLength={2} value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} placeholder="Full name" className={inputCls} />
          <input data-testid="profile-phone-input" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} placeholder="Phone (optional)" className={inputCls} />
          <button data-testid="profile-save-btn" disabled={busyProfile} className="bg-volt text-obsidian font-mono text-[11px] uppercase tracking-[0.2em] font-bold px-6 py-3 hover:bg-[#CCEE00] transition-colors duration-300 disabled:opacity-50">
            {busyProfile ? "Saving..." : "Save Profile"}
          </button>
        </form>
      </section>

      <section className="border border-white/5 bg-panel/40 p-8 h-fit">
        <h3 className="font-display font-semibold text-xl tracking-tight mb-8">Security</h3>
        <form onSubmit={changePw} data-testid="password-form" className="space-y-4">
          <input data-testid="current-password-input" type="password" required placeholder="Current password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className={inputCls} />
          <input data-testid="new-password-input" type="password" required minLength={6} placeholder="New password (min. 6 characters)" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className={inputCls} />
          <button data-testid="change-password-btn" disabled={busyPw} className="border border-volt/50 text-volt font-mono text-[11px] uppercase tracking-[0.2em] px-6 py-3 hover:bg-volt hover:text-obsidian transition-colors duration-300 disabled:opacity-50">
            {busyPw ? "Updating..." : "Change Password"}
          </button>
        </form>
        <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-600 leading-loose">
          Member since {new Date(user.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
        </p>
      </section>
    </div>
  );
}
